// The pre-OTP credential the backend introduced with SEC-114 (Astra-Backend#326).
//
// Before it, `POST /users/login` handed back a full session token *before* the
// verification code was checked, and the second factor was enforced only by
// this app navigating to /verify-code. Anyone holding a phished password could
// skip that screen. Login now answers with a five-minute challenge token whose
// only power is to redeem the code that was just sent; the session is minted by
// `POST /users/otp-verify` once both factors have been presented.
//
// Three screens start a flow that ends at /verify-code — Login, Signup and
// ForgotPassword — and each gets its challenge token from a different response
// shape. This module is the one place that knows where the credential lives, so
// Verification.jsx does not have to care which entrance the user came through.
//
// Kept in sessionStorage, not localStorage: it is scoped to the tab that is
// completing the flow and is worthless afterwards. It is cleared as soon as it
// is redeemed, and on any 401, so a stale value can never be replayed against a
// later flow.

const CHALLENGE_KEY = 'otpChallengeToken';
const PURPOSE_KEY = 'otpChallengePurpose';
const RESET_KEY = 'otpResetToken';

// `purpose` travels WITH the token, deliberately.
//
// What the OTP is being redeemed for is a property of the flow that sent it, so
// it has to share the credential's lifetime — written by the screen that started
// the flow, gone the moment the challenge is spent. Deriving it instead from the
// `verifyCodeFrom` navigation breadcrumb looked equivalent and is not: that
// breadcrumb outlives its flow. Login writes the challenge before it decides
// whether to navigate (and skips the write when getVerifyCodeRouteForUser
// returns null), and ClientDashboard sends an unverified user to /verify-code
// with neither navigation state nor a breadcrumb write. Either way a live login
// challenge can meet a stale `forgotpassword` breadcrumb — which would ask the
// backend to mint a password-reset credential for a login nobody started, and
// cost the user their session too, since /otp-verify returns the reset token
// INSTEAD of minting one.
//
// A write with no purpose clears any previous one, so login and signup reset the
// state simply by storing their own challenge.
export function setChallengeToken(token, { purpose } = {}) {
  if (!token) return;
  try {
    sessionStorage.setItem(CHALLENGE_KEY, token);
    if (purpose) {
      sessionStorage.setItem(PURPOSE_KEY, purpose);
    } else {
      sessionStorage.removeItem(PURPOSE_KEY);
    }
  } catch (_) {
    // Private-mode / quota failures must not break the login flow itself; the
    // verify step will report a missing challenge rather than throwing here.
  }
}

export function getChallengeToken() {
  try {
    return sessionStorage.getItem(CHALLENGE_KEY) || null;
  } catch (_) {
    return null;
  }
}

// What this challenge may be redeemed for: 'reset_password', or null for an
// ordinary verification. Only ForgotPassword sets it.
export function getChallengePurpose() {
  try {
    return sessionStorage.getItem(PURPOSE_KEY) || null;
  } catch (_) {
    return null;
  }
}

export function clearChallengeToken() {
  try {
    sessionStorage.removeItem(CHALLENGE_KEY);
    sessionStorage.removeItem(PURPOSE_KEY);
  } catch (_) {
    // Nothing to do — a value we cannot remove is one we also cannot read.
  }
}

// The single-use token `POST /users/reset-password` requires (SEC-084). It is
// minted by /otp-verify when the OTP is redeemed with `purpose:
// "reset_password"`, which is the server-side proof of the OTP step that used
// to be enforced only by this app's navigation.
export function setResetToken(token) {
  if (!token) return;
  try {
    sessionStorage.setItem(RESET_KEY, token);
  } catch (_) {
    // See setChallengeToken.
  }
}

export function getResetToken() {
  try {
    return sessionStorage.getItem(RESET_KEY) || null;
  } catch (_) {
    return null;
  }
}

export function clearResetToken() {
  try {
    sessionStorage.removeItem(RESET_KEY);
  } catch (_) {
    // See clearChallengeToken.
  }
}
